'use client'

import type { PieSectorShapeProps } from 'recharts'
import { Bar, BarChart, CartesianGrid, Line, LineChart, Pie, PieChart, Sector, XAxis, YAxis } from 'recharts'

import type { RouterOutputs } from '@package/api'
import type { ChartConfig } from '@ui/web/components'
import { ChartContainer, ChartTooltip, ChartTooltipContent, NumberFlow } from '@ui/web/components'
import { typographyVariants } from '@ui/web/lib'
import cn from '@config/tailwind/cn'

type Trend = RouterOutputs['metrics']['getReadingTrend']['trends'][number]
type ActivityByDay = RouterOutputs['metrics']['getReadingTrend']['activityByDay']

/** Minimal shape the activity charts need — satisfied by getReadingTrend and getReadingEngagement alike. */
export interface ActivityPoint {
  date: string
  activeChildren: number
}

/** Minimal shape the consistency charts need: one entry per student. */
interface ConsistencySubject {
  activeDays: number
}

const ACTIVITY_CONFIG = {
  activeChildren: { label: 'Anak aktif', color: 'var(--chart-2)' },
} satisfies ChartConfig

interface ConsistencyBucket<K extends string> {
  key: K
  label: string
  color: string
  test: (pct: number) => boolean
}

/**
 * Consistency bands for ConsistencyChart (the pie on the teacher dashboard, /dasbor).
 *
 * Four 25-point bands on `--chart-*` tokens. This surface already shipped, so the bands and colors are
 * fixed — do not "unify" them with the histogram's. The `key` values are load-bearing: the pie builds
 * its ChartConfig from them and sets each slice's fill to `var(--color-${key})`, which ChartStyle emits
 * from that same config.
 */
const PIE_BUCKETS = [
  { key: 'chart2', label: '<25%', color: 'var(--chart-2)', test: (p: number) => p < 25 },
  { key: 'chart3', label: '25%-50%', color: 'var(--chart-3)', test: (p: number) => p >= 25 && p < 50 },
  { key: 'chart4', label: '50%-75%', color: 'var(--chart-4)', test: (p: number) => p >= 50 && p < 75 },
  { key: 'chart1', label: '>75%', color: 'var(--chart-1)', test: (p: number) => p >= 75 },
] as const

/**
 * Consistency bands for ConsistencyHistogram (the school dashboard).
 *
 * Five 20-point bands on a blue → navy, light → dark ramp, so the bars read as one ordered scale —
 * the same blue/navy-family precedent as `INDEPENDENCE_TIERS` in ../lesson-trend/charts.tsx. The
 * `--chart-*` tokens the pie uses were rejected here because adjacent ones (`--chart-3` #62a3e6 and
 * `--chart-5` #6caae8) are visually indistinguishable, which five bands cannot afford.
 */
const HISTOGRAM_BUCKETS = [
  { key: 'veryLow', label: '<20%', color: 'var(--blue-40)', test: (p: number) => p < 20 },
  { key: 'low', label: '20%-40%', color: 'var(--blue-70)', test: (p: number) => p >= 20 && p < 40 },
  { key: 'moderate', label: '40%-60%', color: 'var(--blue-100)', test: (p: number) => p >= 40 && p < 60 },
  { key: 'high', label: '60%-80%', color: 'var(--navy-70)', test: (p: number) => p >= 60 && p < 80 },
  { key: 'veryHigh', label: '>80%', color: 'var(--navy-100)', test: (p: number) => p >= 80 },
] as const

/** A student at or above this consistency counts as "Konsistensi Baik" — the top two HISTOGRAM_BUCKETS. */
export const GOOD_CONSISTENCY_PCT = 60

/** The pie predates GOOD_CONSISTENCY_PCT and reports a strictly-above-90% figure instead. */
const PIE_GOOD_CONSISTENCY_TEST = (pct: number) => pct > 90

type PieBucketDatum = ConsistencyBucket<(typeof PIE_BUCKETS)[number]['key']> & { value: number; fill: string }

function ConsistencyPieSector(props: PieSectorShapeProps) {
  const fill = (props.payload as PieBucketDatum | undefined)?.fill ?? props.fill
  return <Sector {...props} fill={fill} />
}

function getConsistencyPct(activeDays: number, periodDays: number): number {
  return periodDays > 0 ? (activeDays / periodDays) * 100 : 0
}

interface ConsistencyStats<K extends string> {
  avgPct: number
  avgActiveDays: number
  goodCount: number
  goodPct: number
  /** Count of subjects landing in each band, keyed by the caller's bucket keys. */
  bucketCounts: Record<K, number>
}

/**
 * Consistency figures for either consistency chart: the two footer stats (average % and the
 * count/pct clearing the caller's good-consistency test) plus the per-bucket counts.
 *
 * The bands and the good-consistency test are parameters rather than constants because the two charts
 * legitimately differ — see PIE_BUCKETS and HISTOGRAM_BUCKETS. Keeping one implementation means the
 * two can only diverge where a caller says so, never by one of them drifting.
 */
function getConsistencyStats<K extends string>(
  subjects: ConsistencySubject[],
  periodDays: number,
  buckets: readonly ConsistencyBucket<K>[],
  goodTest: (pct: number) => boolean,
): ConsistencyStats<K> {
  const pcts = subjects.map((s) => getConsistencyPct(s.activeDays, periodDays))
  const avgPct = pcts.length > 0 ? pcts.reduce((sum, p) => sum + p, 0) / pcts.length : 0
  const avgActiveDays = subjects.length > 0 ? subjects.reduce((sum, s) => sum + s.activeDays, 0) / subjects.length : 0
  const goodCount = pcts.filter(goodTest).length
  const goodPct = subjects.length > 0 ? (goodCount / subjects.length) * 100 : 0
  // Total by construction — one entry per bucket — which Object.fromEntries cannot express in its type.
  const bucketCounts = Object.fromEntries(
    buckets.map((bucket) => [bucket.key, pcts.filter((p) => bucket.test(p)).length]),
  ) as Record<K, number>
  return { avgPct, avgActiveDays, goodCount, goodPct, bucketCounts }
}

function formatDayLabel(iso: string): string {
  const d = new Date(iso)
  return d.toLocaleDateString('id-ID', { day: 'numeric', month: 'short' })
}

function toUtcDayKey(date: Date): string {
  const y = date.getUTCFullYear()
  const m = String(date.getUTCMonth() + 1).padStart(2, '0')
  const d = String(date.getUTCDate()).padStart(2, '0')
  return `${y}-${m}-${d}`
}

function getDefaultRange(): { from: Date; to: Date } {
  const to = new Date()
  const from = new Date()
  from.setDate(from.getDate() - 28)
  return { from, to }
}

/**
 * One bar per UTC calendar day in [from, to], filling missing days with zero. Bucketed in UTC to
 * match ReadingMetricsDaily.date (stored at UTC start-of-day) and the UTC window getReadingTrend
 * applies, so every point the API returns lands on a bar — no edge day gets clipped.
 *
 * Takes the structural `ActivityPoint[]` (not `ActivityByDay`) so this helper works for both the
 * router-fed bar chart and the school dashboard's structurally-typed data without either caller
 * depending on the other's router type.
 */
function fillActivityByDayRange(data: ActivityPoint[], from: Date, to: Date): ActivityByDay {
  const byDay = new Map<string, number>()
  for (const point of data) {
    byDay.set(toUtcDayKey(new Date(point.date)), point.activeChildren)
  }

  const cursor = new Date(from)
  cursor.setUTCHours(0, 0, 0, 0)
  const end = new Date(to)
  end.setUTCHours(0, 0, 0, 0)

  if (end.getTime() < cursor.getTime()) {
    return [{ date: cursor.toISOString(), activeChildren: 0 }]
  }

  const result: ActivityByDay = []
  while (cursor.getTime() <= end.getTime()) {
    const key = toUtcDayKey(cursor)
    result.push({
      date: new Date(cursor).toISOString(),
      activeChildren: byDay.get(key) ?? 0,
    })
    cursor.setUTCDate(cursor.getUTCDate() + 1)
  }

  return result
}

export type ActivityGranularity = 'day' | 'week' | 'month'

interface ActivityBucket {
  key: string
  label: string
  meanActiveChildren: number
}

/** Monday-start week containing `date`, in UTC. */
function startOfUtcWeek(date: Date): Date {
  const d = new Date(date)
  const day = d.getUTCDay()
  d.setUTCDate(d.getUTCDate() + (day === 0 ? -6 : 1 - day))
  d.setUTCHours(0, 0, 0, 0)
  return d
}

function startOfUtcMonth(date: Date): Date {
  const d = new Date(date)
  d.setUTCDate(1)
  d.setUTCHours(0, 0, 0, 0)
  return d
}

/**
 * Collapse a gap-filled daily series into day / week / month buckets.
 *
 * The value is the MEAN active children per day inside the bucket, never the sum: a sum would exceed
 * the student count and break the "axis ceiling = number of students" reading. The divisor is the
 * number of days of that bucket present in `filled` — which only spans the selected range — so a
 * partial first or last bucket is not diluted by days outside the range.
 */
export function bucketActivityByGranularity(
  filled: ActivityPoint[],
  granularity: ActivityGranularity,
): ActivityBucket[] {
  if (granularity === 'day') {
    return filled.map((point) => ({
      key: point.date,
      label: formatDayLabel(point.date),
      meanActiveChildren: point.activeChildren,
    }))
  }

  const startOfBucket = granularity === 'week' ? startOfUtcWeek : startOfUtcMonth
  const groups = new Map<string, { start: Date; sum: number; days: number }>()

  for (const point of filled) {
    const start = startOfBucket(new Date(point.date))
    const key = toUtcDayKey(start)
    const group = groups.get(key) ?? { start, sum: 0, days: 0 }
    group.sum += point.activeChildren
    group.days += 1
    groups.set(key, group)
  }

  return [...groups.values()]
    .sort((a, b) => a.start.getTime() - b.start.getTime())
    .map((group) => ({
      key: toUtcDayKey(group.start),
      label: formatDayLabel(group.start.toISOString()),
      meanActiveChildren: group.days > 0 ? group.sum / group.days : 0,
    }))
}

/**
 * Axis ceiling = current student count, but never below the tallest bar so a stale day
 * (e.g. a child deleted after reading) can't clip. Children are whole, so the floor is 1.
 *
 * Takes `ActivityPoint[]` for the same reason as fillActivityByDayRange above.
 */
function getYAxisMax(totalChildren: number, data: ActivityPoint[]): number {
  const dataMax = data.reduce((max, point) => Math.max(max, point.activeChildren), 0)
  return Math.max(1, totalChildren, dataMax)
}

/** Up to ~6 evenly-stepped integer ticks, always ending exactly at `max` (= student count). */
function getYTicks(max: number): number[] {
  const step = Math.max(1, Math.ceil(max / 5))
  const ticks: number[] = []
  for (let value = 0; value < max; value += step) {
    ticks.push(value)
  }
  ticks.push(max)
  return ticks
}

interface ActivitySummary {
  filled: ActivityPoint[]
  yMax: number
  yTicks: number[]
  avgActive: number
  avgPct: number
}

/**
 * Shared activity preamble: gap-fills the series over [from, to] (defaulting to the last 28 days), then
 * derives the y-axis ceiling/ticks and the average-active-children figures. Used by both
 * ReadingActivityChart's daily bars and ReadingActivityLineChart's bucketed line so neither chart can
 * drift from the other's axis or footer math.
 */
function getActivitySummary(data: ActivityPoint[], totalChildren: number, from?: Date, to?: Date): ActivitySummary {
  const range = from && to ? { from, to } : getDefaultRange()
  const filled = fillActivityByDayRange(data, range.from, range.to)
  const yMax = getYAxisMax(totalChildren, filled)
  const yTicks = getYTicks(yMax)
  const avgActive = filled.length > 0 ? filled.reduce((sum, point) => sum + point.activeChildren, 0) / filled.length : 0
  const avgPct = totalChildren > 0 ? (avgActive / totalChildren) * 100 : 0
  return { filled, yMax, yTicks, avgActive, avgPct }
}

/** One labelled figure in a chart card's footer: bold percentage plus a parenthetical note. */
function ChartFooterStat({
  label,
  value,
  maximumFractionDigits,
  note,
}: {
  label: string
  value: number
  maximumFractionDigits: number
  note: string
}) {
  return (
    <div className='srd:flex srd:flex-col srd:gap-y-1'>
      <p className={cn(typographyVariants({ variant: 'label-sm' }), 'srd:text-grey-100')}>{label}</p>
      <p
        className={cn(
          typographyVariants({ variant: 'body-sm' }),
          'srd:flex srd:flex-wrap srd:items-baseline srd:gap-x-1',
        )}
      >
        <NumberFlow
          value={value}
          suffix='%'
          format={{ minimumFractionDigits: 0, maximumFractionDigits }}
          className={cn(typographyVariants({ variant: 'body-md' }), 'srd:font-bold')}
        />
        <span>({note})</span>
      </p>
    </div>
  )
}

export function ReadingActivityChart({
  data,
  totalChildren,
  from,
  to,
}: {
  data: ActivityByDay
  totalChildren: number
  from?: Date
  to?: Date
}) {
  const { filled: chartData, yMax, yTicks, avgActive, avgPct } = getActivitySummary(data, totalChildren, from, to)

  return (
    <div className='srd:flex srd:flex-1 srd:flex-col srd:gap-2 srd:mt-2'>
      <ChartContainer
        config={ACTIVITY_CONFIG}
        className={cn('srd:min-h-[220px] srd:h-auto', 'srd:aspect-auto srd:w-full srd:shrink-0')}
      >
        <BarChart accessibilityLayer data={chartData} margin={{ left: 0, right: 8, top: 16, bottom: 8 }}>
          <CartesianGrid vertical={false} strokeDasharray='4 4' />
          <XAxis
            dataKey='date'
            tickLine={false}
            axisLine={false}
            tickMargin={8}
            tickFormatter={formatDayLabel}
            minTickGap={24}
          />
          <YAxis tickLine={false} axisLine={false} width={32} domain={[0, yMax]} ticks={yTicks} allowDataOverflow />
          <ChartTooltip content={<ChartTooltipContent labelFormatter={(v) => formatDayLabel(String(v))} />} />
          <Bar dataKey='activeChildren' fill='var(--chart-2)' radius={[4, 4, 0, 0]} />
        </BarChart>
      </ChartContainer>

      <div className='srd:mt-auto srd:border-t srd:border-outline-variant/50 srd:pt-2 srd:flex srd:flex-col srd:gap-y-1'>
        <ChartFooterStat
          label='Total rata-rata'
          value={avgPct}
          maximumFractionDigits={2}
          note={`${Math.round(avgActive).toLocaleString('id-ID')} dari ${totalChildren.toLocaleString('id-ID')} anak per hari`}
        />
      </div>
    </div>
  )
}

const ACTIVITY_LINE_CONFIG = {
  meanActiveChildren: { label: 'Anak aktif', color: 'var(--chart-2)' },
} satisfies ChartConfig

/**
 * A custom tooltip `formatter` receives the raw series `name` (recharts falls back to the literal
 * `dataKey` when a `<Line>`/`<Bar>` sets no `name` prop) and bypasses ChartTooltipContent's own
 * `itemConfig?.label ?? item.name` lookup in its default render branch. This resolves the same label
 * from a ChartConfig so the human-readable text keeps a single source of truth in that config object
 * instead of being duplicated as a string literal wherever a formatter is supplied.
 */
function getConfigLabel(config: ChartConfig, name: string | number | undefined) {
  if (name === undefined) return name
  return config[String(name)]?.label ?? name
}

/**
 * Same data and footer as ReadingActivityChart, drawn as a line over day/week/month buckets. Used by
 * the school dashboard; the teacher dashboard keeps the daily bar version.
 */
export function ReadingActivityLineChart({
  data,
  totalChildren,
  from,
  to,
  granularity,
}: {
  data: ActivityPoint[]
  totalChildren: number
  from?: Date
  to?: Date
  granularity: ActivityGranularity
}) {
  // yMax/yTicks come from the daily series inside getActivitySummary: a bucket mean can never exceed
  // the tallest day it contains.
  const { filled, yMax, yTicks, avgActive, avgPct } = getActivitySummary(data, totalChildren, from, to)
  const chartData = bucketActivityByGranularity(filled, granularity)

  return (
    <div className='srd:flex srd:flex-1 srd:flex-col srd:gap-2 srd:mt-2'>
      <ChartContainer
        config={ACTIVITY_LINE_CONFIG}
        className={cn('srd:min-h-[220px] srd:h-auto', 'srd:aspect-auto srd:w-full srd:shrink-0')}
      >
        <LineChart accessibilityLayer data={chartData} margin={{ left: 0, right: 8, top: 16, bottom: 8 }}>
          <CartesianGrid vertical={false} strokeDasharray='4 4' />
          <XAxis dataKey='label' tickLine={false} axisLine={false} tickMargin={8} minTickGap={24} />
          <YAxis tickLine={false} axisLine={false} width={32} domain={[0, yMax]} ticks={yTicks} allowDataOverflow />
          <ChartTooltip
            content={
              <ChartTooltipContent
                formatter={(value, name) => (
                  <div className='srd:flex srd:flex-1 srd:items-center srd:justify-between srd:gap-4'>
                    <span className={cn(typographyVariants({ variant: 'label-sm' }), 'srd:text-grey-100')}>
                      {getConfigLabel(ACTIVITY_LINE_CONFIG, name)}
                    </span>
                    <span className={cn(typographyVariants({ variant: 'label-sm' }), 'srd:font-bold srd:tabular-nums')}>
                      {typeof value === 'number' ? value.toLocaleString('id-ID', { maximumFractionDigits: 1 }) : value}
                    </span>
                  </div>
                )}
              />
            }
          />
          <Line
            dataKey='meanActiveChildren'
            type='monotone'
            stroke='var(--chart-2)'
            strokeWidth={2}
            dot={false}
            activeDot={{ r: 4 }}
          />
        </LineChart>
      </ChartContainer>

      <div className='srd:mt-auto srd:border-t srd:border-outline-variant/50 srd:pt-2 srd:flex srd:flex-col srd:gap-y-1'>
        <ChartFooterStat
          label='Total rata-rata'
          value={avgPct}
          maximumFractionDigits={2}
          note={`${Math.round(avgActive).toLocaleString('id-ID')} dari ${totalChildren.toLocaleString('id-ID')} anak per hari`}
        />
      </div>
    </div>
  )
}

/** Pie of children bucketed by consistency = activeDays / periodDays. */
export function ConsistencyChart({ trends, periodDays }: { trends: Trend[]; periodDays: number }) {
  const stats = getConsistencyStats(trends, periodDays, PIE_BUCKETS, PIE_GOOD_CONSISTENCY_TEST)
  const counts: PieBucketDatum[] = PIE_BUCKETS.map((b) => ({
    ...b,
    value: stats.bucketCounts[b.key],
    fill: `var(--color-${b.key})`,
  }))
  const config = Object.fromEntries(
    counts.map((c) => [c.key, { label: c.label, color: c.color }]),
  ) satisfies ChartConfig

  return (
    <div className='srd:flex srd:flex-1 srd:flex-col srd:gap-2'>
      <ChartContainer
        config={config}
        className={cn('srd:min-h-[180px] srd:h-auto', 'srd:aspect-auto srd:w-full srd:shrink-0')}
      >
        <PieChart>
          <ChartTooltip content={<ChartTooltipContent hideLabel />} />
          <Pie data={counts} dataKey='value' nameKey='label' outerRadius='80%' shape={ConsistencyPieSector} />
        </PieChart>
      </ChartContainer>

      <div
        className={cn(
          'srd:flex srd:shrink-0 srd:flex-wrap srd:items-center srd:justify-center srd:gap-x-3 srd:gap-y-1',
        )}
      >
        {counts.map((c) => (
          <span
            key={c.key}
            className={cn(typographyVariants({ variant: 'label-sm' }), 'srd:flex srd:items-center srd:gap-1')}
          >
            <span className='srd:size-2 srd:rounded-full' style={{ backgroundColor: c.color }} />
            {c.label}
          </span>
        ))}
      </div>

      <div className='srd:mt-auto srd:border-t srd:border-outline-variant/50 srd:pt-2 srd:gap-4 srd:flex srd:flex-wrap'>
        <ChartFooterStat
          label='Total rata-rata'
          value={stats.avgPct}
          maximumFractionDigits={1}
          note={`${Math.round(stats.avgActiveDays).toLocaleString('id-ID')} hari/anak`}
        />
        <ChartFooterStat
          label='Konsistensi >90%'
          value={stats.goodPct}
          maximumFractionDigits={0}
          note={`${stats.goodCount.toLocaleString('id-ID')} anak`}
        />
      </div>
    </div>
  )
}

const CONSISTENCY_HISTOGRAM_CONFIG = {
  students: { label: 'Anak', color: 'var(--chart-1)' },
} satisfies ChartConfig

/**
 * Students bucketed by consistency (activeDays / periodDays), as a bar histogram.
 *
 * Shares getConsistencyStats with ConsistencyChart but not its bands: this chart uses the five
 * HISTOGRAM_BUCKETS and reports "Konsistensi Baik" at GOOD_CONSISTENCY_PCT, where the pie uses four
 * PIE_BUCKETS and reports strictly above 90%. Same figures for the same students only when the
 * parameters agree.
 */
export function ConsistencyHistogram({ students, periodDays }: { students: ConsistencySubject[]; periodDays: number }) {
  const stats = getConsistencyStats(students, periodDays, HISTOGRAM_BUCKETS, (pct) => pct >= GOOD_CONSISTENCY_PCT)
  const chartData = HISTOGRAM_BUCKETS.map((bucket) => ({
    label: bucket.label,
    students: stats.bucketCounts[bucket.key],
    fill: bucket.color,
  }))

  return (
    <div className='srd:flex srd:flex-1 srd:flex-col srd:gap-2 srd:mt-2'>
      <ChartContainer
        config={CONSISTENCY_HISTOGRAM_CONFIG}
        className={cn('srd:min-h-[220px] srd:h-auto', 'srd:aspect-auto srd:w-full srd:shrink-0')}
      >
        <BarChart accessibilityLayer data={chartData} margin={{ left: 0, right: 8, top: 16, bottom: 8 }}>
          <CartesianGrid vertical={false} strokeDasharray='4 4' />
          <XAxis dataKey='label' tickLine={false} axisLine={false} tickMargin={8} />
          <YAxis tickLine={false} axisLine={false} width={32} allowDecimals={false} />
          <ChartTooltip content={<ChartTooltipContent hideLabel />} />
          <Bar dataKey='students' radius={[4, 4, 0, 0]} />
        </BarChart>
      </ChartContainer>

      <div className='srd:mt-auto srd:border-t srd:border-outline-variant/50 srd:pt-2 srd:gap-4 srd:flex srd:flex-wrap'>
        <ChartFooterStat
          label='Total rata-rata'
          value={stats.avgPct}
          maximumFractionDigits={1}
          note={`${Math.round(stats.avgActiveDays).toLocaleString('id-ID')} hari/anak`}
        />
        <ChartFooterStat
          label='Konsistensi Baik'
          value={stats.goodPct}
          maximumFractionDigits={0}
          note={`${stats.goodCount.toLocaleString('id-ID')} anak`}
        />
      </div>
    </div>
  )
}
